import React, { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import apiService from '../../services/api'
import {
  HiOutlineArrowUpTray,
  HiOutlineXMark,
  HiOutlineDocument,
  HiOutlineExclamationTriangle,
  HiOutlineCheckCircle,
  HiOutlineInformationCircle
} from 'react-icons/hi2'

const ClientImportModal = ({ isOpen, onClose, onImportSuccess }) => {
  const { isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError, showWarning } = useAlerts()
  const { getEntity } = useWorkspace()
  
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [importResult, setImportResult] = useState(null)

  if (!isOpen) return null

  const handleFileSelect = (selectedFile) => {
    if (selectedFile) {
      // Validate file type
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel', // .xls
        'text/csv' // .csv
      ]
      
      if (!allowedTypes.includes(selectedFile.type)) {
        showError('Please upload an Excel (.xlsx, .xls) or CSV file')
        return
      }
      
      setFile(selectedFile)
      setImportResult(null)
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    const droppedFile = e.dataTransfer.files[0]
    handleFileSelect(droppedFile)
  }

  const handleFileInputChange = (e) => {
    const selectedFile = e.target.files[0]
    handleFileSelect(selectedFile)
  }

  const handleImport = async () => {
    if (!file) {
      showError('Please select a file to import')
      return
    }

    try {
      setUploading(true)
      
      const formData = new FormData()
      formData.append('file', file)
      
      const params = isWorkspaceSwitched && ownerAccountId 
        ? { owner_account_id: ownerAccountId }
        : {}

      const response = await apiService.post('/clients/import/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        params
      })
      
      if (response.success) {
        setImportResult(response.data)
        
        if (response.data.success_count > 0) {
          showSuccess(`Successfully imported ${response.data.success_count} ${getEntity('client').plural.toLowerCase()}`)
          onImportSuccess?.()
        }
        
        if (response.data.warnings && response.data.warnings.length > 0) {
          showWarning(`Import completed with ${response.data.warnings.length} warnings`)
        }
      } else {
        showError(response.error?.message || 'Import failed')
        setImportResult({
          success: false,
          error: response.error?.message || 'Import failed',
          success_count: 0,
          error_count: 0,
          warnings: []
        })
      }
    } catch (error) {
      console.error('Import error:', error)
      showError('Import failed. Please check your file format and try again.')
      setImportResult({
        success: false,
        error: 'Import failed. Please check your file format and try again.',
        success_count: 0,
        error_count: 0,
        warnings: []
      })
    } finally {
      setUploading(false)
    }
  }

  const handleClose = () => {
    setFile(null)
    setImportResult(null)
    setUploading(false)
    onClose()
  }

  const downloadTemplate = () => {
    // Create CSV template
    const csvContent = `Name,Country Code,Mobile,Email
John Fernandez,+971,524365335,invo@ovacs.com
Ahmad Omar,+963,999123456,sales@ovacs.com`
    
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${getEntity('client').plural.toLowerCase()}_import_template.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={handleClose} />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all sm:w-full sm:max-w-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <HiOutlineArrowUpTray className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Import {getEntity('client').plural}</h3>
                  <p className="text-sm text-gray-500">Upload an Excel or CSV file to import multiple {getEntity('client').plural.toLowerCase()}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-500 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <HiOutlineXMark className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {/* Template Download */}
              <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-start gap-3">
                  <HiOutlineInformationCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Excel File Template</h4>
                    <div className="text-sm text-blue-800 mb-3">
                      <p className="mb-2"><strong>Required columns:</strong></p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li><strong>Name</strong> - {getEntity('client').singular} full name (required)</li>
                        <li><strong>Country Code</strong> - e.g., +971, +963 (optional)</li>
                        <li><strong>Mobile</strong> - Numbers only (optional)</li>
                        <li><strong>Email</strong> - Valid email format (optional)</li>
                      </ul>
                    </div>
                    <button
                      onClick={downloadTemplate}
                      className="text-blue-700 hover:text-blue-800 font-medium text-sm underline"
                    >
                      Download Template File
                    </button>
                  </div>
                </div>
              </div>

              {/* File Upload Area */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  dragOver 
                    ? 'border-blue-400 bg-blue-50' 
                    : file 
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                }`}
              >
                {file ? (
                  <div className="space-y-3">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto">
                      <HiOutlineDocument className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      onClick={() => setFile(null)}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Remove file
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto">
                      <HiOutlineArrowUpTray className="w-6 h-6 text-gray-400" />
                    </div>
                    <div>
                      <p className="text-gray-700 font-medium">Drop your file here or click to browse</p>
                      <p className="text-sm text-gray-500 mt-1">
                        Supports Excel (.xlsx, .xls) and CSV files
                      </p>
                    </div>
                    <input
                      type="file"
                      onChange={handleFileInputChange}
                      accept=".xlsx,.xls,.csv"
                      className="hidden"
                      id="file-input"
                    />
                    <label
                      htmlFor="file-input"
                      className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
                    >
                      Choose File
                    </label>
                  </div>
                )}
              </div>

              {/* Import Result */}
              {importResult && (
                <div className="mt-6">
                  {importResult.success !== false ? (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <HiOutlineCheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-green-900 mb-2">Import Completed</h4>
                          <div className="text-sm text-green-800 space-y-1">
                            <p>✅ Successfully imported: <strong>{importResult.success_count}</strong> {getEntity('client').plural.toLowerCase()}</p>
                            {importResult.error_count > 0 && (
                              <p>❌ Failed to import: <strong>{importResult.error_count}</strong> rows</p>
                            )}
                            {importResult.duplicate_count > 0 && (
                              <p>⚠️ Duplicate entries ignored: <strong>{importResult.duplicate_count}</strong> rows</p>
                            )}
                          </div>
                          
                          {importResult.warnings && importResult.warnings.length > 0 && (
                            <div className="mt-3">
                              <p className="text-sm font-medium text-amber-800 mb-2">Warnings:</p>
                              <ul className="text-sm text-amber-700 space-y-1">
                                {importResult.warnings.map((warning, index) => (
                                  <li key={index}>• {warning}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <HiOutlineExclamationTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-red-900 mb-2">Import Failed</h4>
                          <p className="text-sm text-red-800">{importResult.error}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Validation Rules */}
              <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                <h4 className="text-sm font-medium text-gray-900 mb-3">📋 Validation Rules</h4>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>✅ <strong>Email validation:</strong> Must contain @ and . (if provided)</li>
                  <li>✅ <strong>Duplicate prevention:</strong> Same mobile + email combinations are ignored</li>
                  <li>✅ <strong>Name checking:</strong> Existing {getEntity('client').singular.toLowerCase()} names in your account are ignored</li>
                  <li>✅ <strong>Country codes:</strong> Only valid country codes are accepted</li>
                  <li>⚠️ <strong>Invalid data:</strong> Rows with incorrect data are ignored with warnings</li>
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-100 bg-gray-50">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={uploading}
              >
                {importResult?.success !== false ? 'Close' : 'Cancel'}
              </button>
              
              {!importResult && (
                <button
                  onClick={handleImport}
                  disabled={!file || uploading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {uploading ? (
                    <>
                      <div className="loading-spinner w-4 h-4 inline mr-2"></div>
                      Importing...
                    </>
                  ) : (
                    <>
                      <HiOutlineArrowUpTray className="w-4 h-4 inline mr-2" />
                      Import {getEntity('client').plural}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ClientImportModal
